import type {Component, Editor} from 'grapesjs';
import {MAX_GETTY_IMAGES_PER_EMAIL, MAX_GETTY_VIDEOS_PER_EMAIL, MULTI_MEDIA_SOURCE_GETTY} from '@Libraries/user-media-library';
import {openMessageModal} from '@Components/modals/message-modal';
import {EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {EmailMakerMediaType} from '@EmailMakerReact/email-maker-react.types';
import {getAllComponentsInTree, getComponentMediaType, getStringProp, hasBackgroundImage} from '@EmailMakerReact/lib/email-maker-component-query-library';

/** Getty counts are persisted as props on the PMW content component, serialized into saved templates. */

export interface GettyCounts {
  gettyImageCount: number;
  gettyVideoCount: number;
}

interface CanAddUniqueGettyAssetOpts {
  mediaType: EmailMakerMediaType;
  candidateHashedFilename: string;
  excludeComponent?: Component;
}

const pendingGettyAddsByMediaType = new Map<EmailMakerMediaType, Map<string, number>>();

export function reserveGettyAsset(mediaType: EmailMakerMediaType, hashedFilename: string): void {
  const pendingAdds = pendingGettyAddsByMediaType.get(mediaType) ?? new Map<string, number>();
  pendingAdds.set(hashedFilename, (pendingAdds.get(hashedFilename) ?? 0) + 1);
  pendingGettyAddsByMediaType.set(mediaType, pendingAdds);
}

export function releaseGettyAsset(mediaType: EmailMakerMediaType, hashedFilename: string): void {
  const pendingAdds = pendingGettyAddsByMediaType.get(mediaType);
  if (!pendingAdds) {
    return;
  }

  const count = pendingAdds.get(hashedFilename) ?? 0;
  if (count <= 1) {
    pendingAdds.delete(hashedFilename);
  } else {
    pendingAdds.set(hashedFilename, count - 1);
  }
}

export function canAddUniqueGettyAsset(editor: Editor, opts: CanAddUniqueGettyAssetOpts): boolean {
  const usedHashedFilenames = getUniqueGettyHashedFilenames(editor, opts.mediaType, opts.excludeComponent);
  for (const hashedFilename of pendingGettyAddsByMediaType.get(opts.mediaType)?.keys() ?? []) {
    usedHashedFilenames.add(hashedFilename);
  }

  return usedHashedFilenames.has(opts.candidateHashedFilename) || usedHashedFilenames.size < getGettyLimitForMediaType(opts.mediaType);
}

export function warnIfGettyLimitsExceeded(editor: Editor): void {
  for (const mediaType of [EmailMakerMediaType.IMAGE, EmailMakerMediaType.VIDEO]) {
    if (getUniqueGettyHashedFilenames(editor, mediaType).size > getGettyLimitForMediaType(mediaType)) {
      showGettyLimitMessage(mediaType);
    }
  }
}

export function isEmailContentMounted(editor: Editor): boolean {
  return getMainContentComponent(editor)?.getEl()?.isConnected === true;
}

export function getUniqueGettyHashedFilenames(editor: Editor, mediaType: EmailMakerMediaType, excludeComponent?: Component): Set<string> {
  const hashedFilenames = new Set<string>();
  const wrapper = editor.DomComponents.getWrapper();

  if (!wrapper) {
    return hashedFilenames;
  }

  for (const component of getAllComponentsInTree(wrapper)) {
    if (component === excludeComponent || !isGettyMediaOfType(editor, component, mediaType)) {
      continue;
    }

    const hashedFilename = getStringProp(component, EMAIL_MAKER_PROPS.HASHED_FILENAME);
    if (hashedFilename) {
      hashedFilenames.add(hashedFilename);
    }
  }

  return hashedFilenames;
}

export function syncGettyCountForComponents(editor: Editor): void {
  updateGettyCountsForEmail(editor, {
    gettyImageCount: getUniqueGettyHashedFilenames(editor, EmailMakerMediaType.IMAGE).size,
    gettyVideoCount: getUniqueGettyHashedFilenames(editor, EmailMakerMediaType.VIDEO).size,
  });
}

export function updateGettyCountsForEmail(editor: Editor, counts: GettyCounts): void {
  const mainContent = getMainContentComponent(editor);
  if (!mainContent) {
    return;
  }

  mainContent.set(EMAIL_MAKER_PROPS.PREMIUM_IMAGE_COUNT, counts.gettyImageCount);
  mainContent.set(EMAIL_MAKER_PROPS.PREMIUM_VIDEO_COUNT, counts.gettyVideoCount);
}

export function showGettyLimitMessage(mediaType: EmailMakerMediaType, onClose?: VoidFunction): void {
  const isImage = mediaType === EmailMakerMediaType.IMAGE;

  openMessageModal({
    title: isImage ? window.i18next.t('pmwjs_getty_limit') : window.i18next.t('pmwjs_getty_video_limit'),
    text: isImage
      ? window.i18next.t('pmwjs_getty_limit_message', {limit: MAX_GETTY_IMAGES_PER_EMAIL})
      : window.i18next.t('pmwjs_video_getty_limit_message', {num: MAX_GETTY_VIDEOS_PER_EMAIL}),
    onClose,
  });
}

function getGettyLimitForMediaType(mediaType: EmailMakerMediaType): number {
  return mediaType === EmailMakerMediaType.VIDEO ? MAX_GETTY_VIDEOS_PER_EMAIL : MAX_GETTY_IMAGES_PER_EMAIL;
}

function isGettyMediaOfType(editor: Editor, component: Component, mediaType: EmailMakerMediaType): boolean {
  if (component.get(EMAIL_MAKER_PROPS.CONTENT_SOURCE) !== MULTI_MEDIA_SOURCE_GETTY) {
    return false;
  }

  const hasGraphic =
    component.get('type') === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE ||
    component.get('type') === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO ||
    hasBackgroundImage(component);

  if (!hasGraphic) {
    return false;
  }

  return getComponentMediaType(editor, component) === mediaType;
}

/** The counts only exist once the template content has mounted. */
function getMainContentComponent(editor: Editor): Component | undefined {
  const wrapper = editor.DomComponents.getWrapper();
  if (!wrapper || !editor.getComponents().models.length) {
    return undefined;
  }

  return wrapper.find(`[data-gjs-type="${EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT}"]`)[0];
}
